import { __extends } from "tslib";
import React from 'react';
import classnames from 'classnames';
import { compose } from 'redux';
import throttle from 'lodash/throttle';
import ErrorBoundary from '../ErrorBoundary';
import ArrowLeftIconNew from '../Icons/ArtStageArrowL';
import ArrowRightIconNew from '../Icons/ArtStageArrowR';
import { BiEventType } from '../biLogger';
import { isLoggableClickEvent } from '../biLogger/hooks/useBiClickLogger';
import { withLogBiEvent, } from '../biLogger/hocs/withLogBiEvent';
import { withMobileContext } from '../Context';
import { SCROLL_THROTTLE_TIMEOUT } from './constants';
import LazyContext from '../LazyLoadingImage/LazyContext';
import s from './Slider.scss';
var enhance = compose(withMobileContext, withLogBiEvent);
var Slider = /** @class */ (function (_super) {
    __extends(Slider, _super);
    function Slider() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.rootRef = React.createRef();
        _this.contentRef = React.createRef();
        _this.state = { scrollToIndex: 0, overflow: '', hintDone: false };
        _this.setOverflow = throttle(function () {
            var overflow = '';
            if (!_this.contentRef.current) {
                return overflow;
            }
            var contentRect = _this.contentRef.current.getBoundingClientRect();
            var scrollLeft = _this.contentRef.current.scrollLeft;
            var scrollWidth = _this.contentRef.current.scrollWidth;
            var scrollRight = scrollWidth - scrollLeft - contentRect.width;
            if (scrollLeft && scrollRight) {
                overflow = 'both';
            }
            else if (scrollLeft) {
                overflow = 'left';
            }
            else if (scrollRight) {
                overflow = 'right';
            }
            if (_this.state.overflow !== overflow) {
                _this.setState({ overflow: overflow });
            }
        }, SCROLL_THROTTLE_TIMEOUT, { leading: true });
        _this.handleScroll = throttle(function () {
            _this.setState({ hintDone: true });
            _this.setOverflow();
            if (_this.props.onScroll) {
                // available width to scroll next
                var contentRect = _this.contentRef.current.getBoundingClientRect();
                var scrollRight = _this.contentRef.current.scrollWidth -
                    _this.contentRef.current.scrollLeft -
                    contentRect.width;
                _this.props.onScroll(scrollRight);
            }
        }, SCROLL_THROTTLE_TIMEOUT, { leading: true });
        _this.handleScrollToIndex = function () {
            return requestAnimationFrame(function () {
                var smoothInitialScroll = _this.props.smoothInitialScroll;
                var contentRef = _this.contentRef.current;
                var rootRef = _this.rootRef.current;
                if (!contentRef) {
                    return;
                }
                var currentScrollLeft = contentRef.scrollLeft;
                // if scrollToIndex value is not valid - get the last item index
                var scrollToIndex = _this.state.scrollToIndex > contentRef.children.length - 1
                    ? contentRef.children.length - 1
                    : _this.state.scrollToIndex;
                var scrollToChild = contentRef.children[scrollToIndex];
                if (!scrollToChild || !rootRef) {
                    return;
                }
                var scrollToChildRect = scrollToChild.getBoundingClientRect();
                var rootReact = rootRef.getBoundingClientRect();
                // is child to scroll to already visible
                var isVisible = scrollToChildRect.left >= rootReact.left &&
                    scrollToChildRect.right <= rootReact.right;
                if (isVisible) {
                    return;
                }
                var scrollLeft = contentRef.children[scrollToIndex].offsetLeft -
                    contentRef.children[0].offsetLeft;
                if (scrollLeft !== currentScrollLeft) {
                    if (smoothInitialScroll) {
                        contentRef.scrollLeft = scrollLeft;
                    }
                    else {
                        // scrollTo with a behavior parameter doesn't
                        // behave if the behavior is set in CSS...
                        contentRef.style.scrollBehavior = 'auto';
                        contentRef.scrollLeft = scrollLeft;
                        contentRef.style.scrollBehavior = 'smooth';
                    }
                }
            });
        };
        _this.handleScrollBtnClick = function (e, direction) {
            var logBiEvent = _this.props.logBiEvent;
            _this.setState({ hintDone: true });
            // capture this here so we don't need to use e.persist
            var isLoggable = isLoggableClickEvent(e);
            requestAnimationFrame(function () {
                var contentRef = _this.contentRef.current;
                var scrollBy = _this.props.scrollBy;
                if (!contentRef || !scrollBy) {
                    return;
                }
                var contentWidth = contentRef.getBoundingClientRect().width;
                if (logBiEvent && isLoggable) {
                    var scrollableArea = contentRef.scrollWidth - contentWidth;
                    var perc = contentRef.scrollLeft / scrollableArea;
                    logBiEvent({
                        evid: BiEventType.CAROUSEL_ARROW_CLICK,
                        scroll_pct: Math.round(100 * perc),
                        direction: direction,
                    });
                }
                var scrollLeft = scrollBy <= 1 ? contentWidth * scrollBy : scrollBy;
                if (direction === 'prev') {
                    contentRef.scrollLeft -= scrollLeft;
                }
                else if (direction === 'next') {
                    contentRef.scrollLeft += scrollLeft;
                }
            });
        };
        return _this;
    }
    Slider.getDerivedStateFromProps = function (nextProps, prevState) {
        if (nextProps.scrollToIndex !== undefined &&
            nextProps.scrollToIndex !== prevState.scrollToIndex) {
            return { scrollToIndex: nextProps.scrollToIndex };
        }
        return null;
    };
    Slider.prototype.componentDidMount = function () {
        this.setOverflow();
        this.handleScrollToIndex();
        window.addEventListener('resize', this.setOverflow);
        this.contentRef.current.addEventListener('scroll', this.handleScroll);
    };
    Slider.prototype.componentDidUpdate = function (prevProps, prevState) {
        if (this.state.scrollToIndex !== prevState.scrollToIndex) {
            this.handleScrollToIndex();
        }
        this.setOverflow();
    };
    Slider.prototype.componentWillUnmount = function () {
        window.removeEventListener('resize', this.setOverflow);
        this.contentRef.current.removeEventListener('scroll', this.handleScroll);
    };
    Slider.prototype.render = function () {
        var _a = this.props, className = _a.className, height = _a.height, children = _a.children, disableScroll = _a.disableScroll, isMobile = _a.isMobile, slimSpacing = _a.slimSpacing, permArrows = _a.permArrows, dataHook = _a.dataHook, contentClassName = _a.contentClassName, disableArrows = _a.disableArrows;
        var overflow = this.state.overflow;
        var withControls = !isMobile && !disableArrows;
        return (React.createElement(LazyContext.Provider, { value: true },
            React.createElement("div", { className: classnames(s['root'], overflow && s["overflow-" + overflow], slimSpacing && s['slim-spacing'], permArrows && s['perm-arrows'], className), style: { height: height }, ref: this.rootRef, "data-hook": dataHook },
                React.createElement(ErrorBoundary, null,
                    React.createElement("div", { className: classnames(s['content'], disableScroll && s['no-scroll'], contentClassName), ref: this.contentRef, draggable: "false" }, children),
                    withControls && this.renderControls()))));
    };
    Slider.prototype.renderControls = function () {
        var _this = this;
        var _a = this.props, prevButtonClassName = _a.prevButtonClassName, prevIconClassName = _a.prevIconClassName, nextButtonClassName = _a.nextButtonClassName, nextIconClassName = _a.nextIconClassName;
        return (React.createElement(React.Fragment, null,
            React.createElement("button", { className: classnames(s['prev-btn'], prevButtonClassName), onClick: function (e) { return _this.handleScrollBtnClick(e, 'prev'); }, "aria-hidden": 'true' },
                React.createElement(ArrowLeftIconNew, { withPadding: false, className: classnames(s['arrow-icon'], prevIconClassName) })),
            React.createElement("button", { className: classnames(s['next-btn'], nextButtonClassName), onClick: function (e) { return _this.handleScrollBtnClick(e, 'next'); }, "aria-hidden": 'true' },
                React.createElement(ArrowRightIconNew, { withPadding: false, className: classnames(s['arrow-icon'], nextIconClassName) }))));
    };
    Slider.defaultProps = {
        smoothInitialScroll: true,
        scrollBy: 0.5,
    };
    return Slider;
}(React.Component));
export { Slider };
export default enhance(Slider);
//# sourceMappingURL=Slider.js.map